import { useCallback, useMemo, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Add as AddIcon } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useOidc } from '@nohara-dxdev/react-oidc';

import { Site } from '../types/site.types';
import SiteTable from '../components/table/site-table';
import { useProperties } from '../hooks/use-properties';
import { usePropertySearchStore } from '../stores/property-search.store';
import {
  PageContainer,
  PageHeader,
  TitleSection,
  ActionButtonContainer,
  CreateSiteButton,
} from '../styles';

import {
  getSearchConfig,
  initializePropertiesSearchConfig,
  reloadStaffOptions,
  reloadPropertyOptions,
  getDepartmentIdByName,
} from '@/config/search-configs';
import { PageTitle } from '@/components/styles';
import { GlobalSearch } from '@/components/search';
import { Pagination } from '@/components/pagination';
import { paths } from '@/config/paths';
import { checkPermission } from '@/utils/helpers';
import { FunctionName, PermissionAction } from '@/lib/enums';
import { useIsMobile } from '@/hooks/use-is-mobile';

export const PropertiesPage = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchConfigLoaded, setSearchConfigLoaded] = useState(false);
  const [isLoadingStaffOptions, setIsLoadingStaffOptions] = useState(false);
  const [isLoadingPropertyOptions, setIsLoadingPropertyOptions] = useState(false);
  const previousDepartmentRef = useRef<string | undefined>(undefined);
  const previousIncludeArchiveRef = useRef<boolean>(false);
  const departmentChangeTimeoutRef = useRef<number | null>(null);

  const {
    searchResults,
    loading,
    currentPage,
    itemsPerPage,
    totalItems,
    selectedSites,
    sortField,
    sortOrder,
    handleSortChange,
    handleSearch,
    handleSearchChange,
    handleClear,
    handlePageChange,
    handleItemsPerPageChange,
    handleSelectionChange,
    handleArchive,
    handleRestore,
    handleRegisterAutocompleteClear,
  } = useProperties();

  // Get search state from store
  const searchInputValues = usePropertySearchStore((state) => state.searchInputValues);
  const committedSearchParams = usePropertySearchStore((state) => state.committedSearchParams);

  const { permissions } = useOidc();

  const canCreateProperty = useMemo(() => {
    return checkPermission(permissions, FunctionName.PROPERTY_MANAGEMENT, PermissionAction.CREATE);
  }, [permissions]);

  // Handle navigate to property detail
  const handleNavigateToSiteDetail = useCallback(
    (site: Site) => {
      navigate(paths.property.getHref(site.id));
    },
    [navigate]
  );

  // Handle create new site
  const handleCreateSite = useCallback(() => {
    navigate(paths.propertiesRegister.path);
  }, [navigate]);

  // Handle bulk archive selected sites
  const handleArchiveSelected = useCallback(() => {
    if (selectedSites.length === 0) return;
    void handleArchive(selectedSites);
  }, [selectedSites, handleArchive]);

  // Handle bulk restore selected sites
  const handleRestoreSelected = useCallback(() => {
    if (selectedSites.length === 0) return;
    void handleRestore(selectedSites);
  }, [selectedSites, handleRestore]);

  // Wrap handleSearchChange to detect department and includeArchive changes
  const handleSearchChangeWithDepartmentDetection = useCallback(
    (params: Record<string, unknown>) => {
      // Check if department changed
      const currentDepartment = params.department as string | undefined; // This is department name (text)
      const departmentChanged = currentDepartment !== previousDepartmentRef.current;

      // Check if includeArchive changed
      const currentIncludeArchive = params.includeArchive === true;
      const includeArchiveChanged = currentIncludeArchive !== previousIncludeArchiveRef.current;

      // Handle department change - reload staff options without clearing field values
      if (departmentChanged) {
        const departmentId = getDepartmentIdByName(currentDepartment) || undefined;
        if (!departmentId) {
          previousDepartmentRef.current = currentDepartment;
          handleSearchChange(params);
          return;
        }

        previousDepartmentRef.current = currentDepartment;

        // Clear existing timeout to debounce the department change
        if (departmentChangeTimeoutRef.current) {
          window.clearTimeout(departmentChangeTimeoutRef.current);
        }

        // Set a debounced timeout to reload staff options after user stops typing
        departmentChangeTimeoutRef.current = window.setTimeout(() => {
          // Do NOT clear staff field values - keep user's input for better UX
          // Only reload the options list

          // Reload staff options for the new department
          // Pass department name directly (not ID)
          setIsLoadingStaffOptions(true);
          reloadStaffOptions(currentDepartment || undefined)
            .then(() => {})
            .catch((error) => {
              console.error('Error reloading staff options:', error);
            })
            .finally(() => {
              setIsLoadingStaffOptions(false);
            });
        }, 1500); // 1500ms debounce - only call API after user stops typing
      }

      // Handle includeArchive change - reload property options without clearing field value
      if (includeArchiveChanged) {
        previousIncludeArchiveRef.current = currentIncludeArchive;

        // Do NOT clear site_name field value - keep user's input for better UX
        // Only reload the options list

        // Reload property options with new archive status
        setIsLoadingPropertyOptions(true);
        reloadPropertyOptions(FunctionName.PROPERTY_MANAGEMENT, currentIncludeArchive)
          .then(() => {})
          .catch((error) => {
            console.error('Error reloading property options:', error);
          })
          .finally(() => {
            setIsLoadingPropertyOptions(false);
          });
      }

      // Call onChange with original params - keep all field values
      handleSearchChange(params);
    },
    [handleSearchChange]
  );

  // Wrap handleClear to reset refs and reload all options
  const handleClearWithReset = useCallback(() => {
    previousDepartmentRef.current = undefined;
    previousIncludeArchiveRef.current = false;
    handleClear();

    // Reload staff and property options with default filters
    setIsLoadingStaffOptions(true);
    setIsLoadingPropertyOptions(true);

    Promise.all([
      reloadStaffOptions(''),
      reloadPropertyOptions(FunctionName.PROPERTY_MANAGEMENT, false), // Reset to active properties only
    ])
      .then(() => {})
      .catch((error) => {
        console.error('Error resetting options:', error);
      })
      .finally(() => {
        setIsLoadingStaffOptions(false);
        setIsLoadingPropertyOptions(false);
      });
  }, [handleClear]);

  // Common pagination props
  const paginationProps = useMemo(
    () => ({
      currentPage: currentPage - 1,
      totalPages: Math.max(1, Math.ceil(totalItems / itemsPerPage)),
      rowsPerPage: itemsPerPage,
      totalCount: totalItems,
      onPageChange: (page: number) => handlePageChange(page + 1),
      onRowsPerPageChange: handleItemsPerPageChange,
      rowsPerPageOptions: [20, 50, 100],
      showArchiveButtons: true,
      selectedCount: selectedSites.length,
      apiResponseInfo: {
        currentPage: currentPage - 1,
        totalPages: Math.max(1, Math.ceil(totalItems / itemsPerPage)),
        isLoading: loading,
      },
    }),
    [
      currentPage,
      totalItems,
      itemsPerPage,
      handlePageChange,
      handleItemsPerPageChange,
      selectedSites,
      loading,
    ]
  );

  // Set default itemsPerPage based on device type
  useEffect(() => {
    const defaultItemsPerPage = isMobile ? 20 : 100;
    if (itemsPerPage !== defaultItemsPerPage) {
      handleItemsPerPageChange(defaultItemsPerPage);
    }
  }, [isMobile]); // Run when isMobile changes

  // Initialize search config options on mount
  useEffect(() => {
    const initOptions = async () => {
      await initializePropertiesSearchConfig();
      setSearchConfigLoaded(true);
    };
    void initOptions();
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (departmentChangeTimeoutRef.current) {
        window.clearTimeout(departmentChangeTimeoutRef.current);
      }
    };
  }, []);

  const searchConfig = useMemo(
    () => ({
      ...getSearchConfig('properties'),
      initialValues: searchInputValues, // Pass initial values from store to restore form fields
      onSearch: (params: Record<string, unknown>) => {
        handleSearch({ ...params, sort: sortField, orderBy: sortOrder });
      },
      onChange: (params: Record<string, unknown>) => {
        handleSearchChangeWithDepartmentDetection(params);
      },
      onClear: handleClearWithReset,
      loading: loading || !searchConfigLoaded || isLoadingStaffOptions || isLoadingPropertyOptions,
      disabled: !searchConfigLoaded,
    }),
    [
      handleSearch,
      handleSearchChangeWithDepartmentDetection,
      handleClearWithReset,
      loading,
      searchConfigLoaded,
      isLoadingStaffOptions,
      isLoadingPropertyOptions,
      sortField,
      sortOrder,
      searchInputValues,
    ]
  );

  // Restore search when navigating back - trigger search if there are committed params
  useEffect(() => {
    // Trigger search if there are committed params (form fields are restored via initialValues)
    // Preserve page number when restoring
    if (Object.keys(committedSearchParams).length > 0) {
      handleSearch(committedSearchParams, true); // Preserve page number
    }
  }, []); // Only run on mount

  return (
    <PageContainer>
      <PageHeader>
        <TitleSection>
          <PageTitle variant='h6'>現場一覧</PageTitle>
        </TitleSection>
        {canCreateProperty && (
          <ActionButtonContainer>
            <CreateSiteButton
              variant='contained'
              startIcon={<AddIcon />}
              onClick={handleCreateSite}
            >
              現場登録
            </CreateSiteButton>
          </ActionButtonContainer>
        )}
      </PageHeader>

      <GlobalSearch config={searchConfig} clearAutocomplete={handleRegisterAutocompleteClear} />

      {/* Pagination with Archive/Restore Buttons */}
      <Box sx={{ mt: 1.5 }}>
        {!isMobile ? (
          <Pagination
            {...paginationProps}
            onArchive={handleArchiveSelected}
            onRestore={handleRestoreSelected}
          />
        ) : (
          <Pagination {...paginationProps} showPageNavigation={false} />
        )}
      </Box>
      <Box sx={{ mt: 1.5 }}>
        <SiteTable
          data={searchResults}
          loading={loading}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          selectedSites={selectedSites}
          sortField={sortField}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onSelectionChange={handleSelectionChange}
          onArchive={handleArchive}
          onRestore={handleRestore}
          onSiteClick={handleNavigateToSiteDetail}
          isMobile={isMobile}
        />
      </Box>
      {isMobile && (
        <Box sx={{ mt: 1.5 }}>
          <Pagination {...paginationProps} showPerPageSelector={false} showRecordsInfo={false} />
        </Box>
      )}
    </PageContainer>
  );
};
